import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Star, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';   // TikTok icon
import { copy } from '../../lib/copy';
import { useSiteSettings } from '../../lib/useSiteSettings';
import { formatPhoneHref } from '../../lib/phone';

const Footer = () => {
  const biz = useSiteSettings();
  // Footer logo falls back to the general light logo, then the static
  // asset, if nothing's been uploaded specifically for the footer slot.
  const footerLogo = biz.footerLogoUrl || biz.logoLightUrl || biz.logoUrl || "/logo-hsec.png";
  return (
    <footer className="bg-slate-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={footerLogo}
                alt={biz.name}
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold text-lg">{biz.name}</span>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Sydney's most trusted professional cleaning service. Licensed, insured, and committed to exceeding your expectations with every job.
            </p>
            {/* Same authoritative Google Business Profile fields
                Testimonials.jsx uses - real verified figures only, never
                a guessed/hardcoded number, and hidden entirely (not
                replaced with a placeholder) until Admin has real values. */}
            {(Number(biz.googleRating) > 0 || Number(biz.googleReviewCount) > 0) && (
              <div className="flex items-center gap-2 text-yellow-400">
                <Star className="w-4 h-4 fill-current" />
                <span className="text-sm font-semibold">
                  {Number(biz.googleRating) > 0 ? `${Number(biz.googleRating).toFixed(1)} Stars` : "Google Rated"}
                  {Number(biz.googleReviewCount) > 0 ? ` • ${biz.googleReviewCount} Reviews` : ""}
                </span>
              </div>
            )}

            {/* Socials Section */}
            <div className="flex gap-4 mt-3">
              {biz.socials.facebook && (
                <a 
                  href={biz.socials.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-blue-500 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {biz.socials.instagram && (
                <a 
                  href={biz.socials.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-pink-500 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {biz.socials.tiktok && (
                <a 
                  href={biz.socials.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-gray-200 transition-colors"
                  aria-label="TikTok"
                >
                  <FaTiktok className="w-5 h-5" />
                </a>
              )}
              {biz.socials.linkedin && (
                <a
                  href={biz.socials.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-blue-400 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
              {biz.socials.youtube && (
                <a
                  href={biz.socials.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-red-500 transition-colors"
                  aria-label="YouTube"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="font-semibold text-lg">Our Services</h4>
            <ul className="space-y-2">
              {copy.services.map((service) => (
                <li key={service.slug}>
                  <Link 
                    to={`/services/${service.slug}`}
                    className="text-slate-300 hover:text-blue-400 transition-colors text-sm"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-lg">Quick Links</h4>
            <ul className="space-y-2">
              {copy.nav
                .filter((item) => item.label !== "Gallery" && item.label !== "Areas")
                .map((item) => (
                  <li key={item.href}>
                    <Link 
                      to={item.href.replace('/#', '/')}
                      className="text-slate-300 hover:text-blue-400 transition-colors text-sm"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          {/* Contact Info + Map */}
          <div className="space-y-4">
            <h4 className="font-semibold text-lg">Contact Info</h4>
            <div className="space-y-3">
              <a 
                href={formatPhoneHref(biz.phone)}
                className="flex items-center gap-3 text-slate-300 hover:text-blue-400 transition-colors group"
              >
                <Phone className="w-4 h-4 group-hover:text-blue-400" />
                <span className="text-sm">{biz.phone}</span>
              </a>
              <a 
                href={`mailto:${biz.email}`}
                className="flex items-center gap-3 text-slate-300 hover:text-blue-400 transition-colors group"
              >
                <Mail className="w-4 h-4 group-hover:text-blue-400" />
                <span className="text-sm">{biz.email}</span>
              </a>
              <div className="flex items-center gap-3 text-slate-300">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">{biz.address}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{biz.hours}</span>
              </div>
            </div>

            {/* Map/location signal - was a hardcoded Melbourne address the
                text line above it never matched (that text is
                biz.address, "Sydney NSW"). Preference order: a real
                Google Maps/Business Profile URL (Admin > Website Settings
                > Google Business Profile) shown as a direct link - an
                arbitrary Maps share URL isn't reliably embeddable in an
                iframe (Google frequently blocks framing regular share
                links), so linking out is the safe choice; falling back to
                the registered-office address as an actual embed (Google's
                plain ?q=<address>&output=embed format needs no API key,
                unlike a full search/autocomplete picker). Hidden entirely
                if neither is set, rather than a guessed address. */}
            {biz.googleMapsUrl ? (
              <a
                href={biz.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-blue-400 transition-colors"
              >
                <MapPin className="w-4 h-4" /> View on Google Maps
              </a>
            ) : biz.officeAddress && (
              <div className="mt-4">
                <p className="text-xs text-slate-400 mb-1.5">{biz.officeLabel}</p>
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    title={biz.officeLabel || "Map"}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(biz.officeAddress)}&output=embed`}
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen={true}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Service Areas */}
        <div className="mt-8 pt-8 border-t border-slate-800">
          <h4 className="font-semibold text-lg mb-4">Service Areas</h4>
          <div className="text-slate-300 text-sm">
            <p className="leading-relaxed">
              Serving all Sydney suburbs including: {biz.serviceAreas.slice(0, 20).join(', ')}, and many more.
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} {biz.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-slate-400 text-sm">
            <span>ACN: 670 852 963</span>
            <span>Licensed & Insured</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
